import React, { useState, useCallback, useRef } from 'react';
import { Box, Container, Grid, Typography, Button } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ICompanyDetails } from '../../redux/company/types';
import { getJWtDetails } from '../../utils/AppUtils';
import { Edit, Save, X, Info, FileText, Briefcase, Users, Mail, Video, Images } from 'lucide-react';
import {
    CompanyBasicInfoEdit,
    CompanyAboutEdit,
    CompanyServicesEdit,
    CompanyLeadershipEdit,
    CompanyContactEdit,
    CompanyVideosEdit,
    CompanyGalleryEdit,
} from './EditSections';
import { COMPANY_BRAND_ON_ORANGE, COMPANY_BRAND_ORANGE } from '../../constants/companyBrand';

interface ICompanyEditContainer {
    companyDetails?: ICompanyDetails;
    onCancel?: () => void;
}

type EditSection = 'basic' | 'about' | 'services' | 'leadership' | 'contact' | 'videos' | 'gallery';

const sections: { key: EditSection; label: string; Icon: typeof Info }[] = [
    { key: 'basic', label: 'Basic Information', Icon: Info },
    { key: 'about', label: 'About & Overview', Icon: FileText },
    { key: 'services', label: 'Services & Industry', Icon: Briefcase },
    { key: 'leadership', label: 'Team Management', Icon: Users },
    { key: 'contact', label: 'Contact Information', Icon: Mail },
    { key: 'videos', label: 'Company Videos', Icon: Video },
    { key: 'gallery', label: 'Company Gallery', Icon: Images },
];

const CompanyEditContainer: React.FC<ICompanyEditContainer> = ({ companyDetails, onCancel }) => {
    const { themeStyle } = useThemeContext();
    const [activeSection, setActiveSection] = useState<EditSection>('basic');
    const [isEditing, setIsEditing] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const currentSectionSaveRef = useRef<(() => void) | null>(null);

    const { user_id } = getJWtDetails();
    const isCompanyCreator = user_id === companyDetails?.company?.created_by?._id;

    const handleSave = useCallback(() => {
        if (currentSectionSaveRef.current) {
            currentSectionSaveRef.current();
        }
        setIsEditing(false);
        setHasChanges(false);
    }, []);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        setHasChanges(false);
        onCancel?.();
    }, [onCancel]);

    const handleSectionChanges = useCallback((hasChanges: boolean) => {
        setHasChanges(hasChanges);
    }, []);

    const handleSectionSave = useCallback((saveFunction: () => void) => {
        currentSectionSaveRef.current = saveFunction;
    }, []);

    if (!isCompanyCreator) {
        return (
            <Container
                sx={{
                    display: 'flex',
                    width: '100vw',
                    height: '60vh',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                <Typography sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    You don't have permission to edit this company
                </Typography>
            </Container>
        );
    }

    const renderSection = () => {
        switch (activeSection) {
            case 'basic':
                return (
                    <CompanyBasicInfoEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'about':
                return (
                    <CompanyAboutEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'services':
                return (
                    <CompanyServicesEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'leadership':
                return (
                    <CompanyLeadershipEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                        leadership={companyDetails?.leadership}
                    />
                );
            case 'contact':
                return (
                    <CompanyContactEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'videos':
                return (
                    <CompanyVideosEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'gallery':
                return (
                    <CompanyGalleryEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Container
            disableGutters
            sx={{
                width: '100%',
                marginLeft: 0,
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 2, sm: 3 },
                overflowX: 'hidden',
                minHeight: '70vh',
                backgroundColor: theme[themeStyle].pure_black,
            }}
            maxWidth={false}>
            <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 20,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 2,
                        py: 2,
                        mb: 1,
                        bgcolor: theme[themeStyle].pure_black,
                    }}>
                    {!isEditing ? (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<X size={16} />}
                                onClick={handleCancel}
                                sx={{
                                    borderRadius: 9999,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: '#ffffff',
                                    borderColor: 'rgba(255,255,255,0.35)',
                                    px: 3,
                                    '&:hover': {
                                        borderColor: '#ffffff',
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                    },
                                }}>
                                Cancel
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<Edit size={16} />}
                                onClick={() => setIsEditing(true)}
                                sx={{
                                    borderRadius: 9999,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: '#ffffff',
                                    borderColor: 'rgba(255,255,255,0.35)',
                                    px: 3,
                                    '&:hover': {
                                        borderColor: COMPANY_BRAND_ORANGE,
                                        color: COMPANY_BRAND_ORANGE,
                                        bgcolor: 'rgba(255,156,40,0.08)',
                                    },
                                }}>
                                Edit
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<X size={16} />}
                                onClick={handleCancel}
                                sx={{
                                    borderRadius: 9999,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: '#ffffff',
                                    borderColor: 'rgba(255,255,255,0.35)',
                                    px: 3,
                                    '&:hover': {
                                        borderColor: '#ffffff',
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                    },
                                }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Save size={14} />}
                                onClick={handleSave}
                                disabled={!hasChanges}
                                disableElevation
                                sx={{
                                    borderRadius: 9999,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    px: { xs: 2.5, sm: 4 },
                                    minHeight: { xs: 38, sm: 44 },
                                    fontSize: { xs: '0.825rem', sm: '0.95rem' },
                                    bgcolor: hasChanges ? COMPANY_BRAND_ORANGE : 'rgba(255,255,255,0.12)',
                                    color: hasChanges ? COMPANY_BRAND_ON_ORANGE : 'rgba(255,255,255,0.35)',
                                    boxShadow: hasChanges ? '0 8px 24px rgba(255,156,40,0.25)' : 'none',
                                    '&:hover': {
                                        bgcolor: hasChanges ? '#e88f22' : 'rgba(255,255,255,0.12)',
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: 'rgba(255,255,255,0.08)',
                                        color: 'rgba(255,255,255,0.3)',
                                    },
                                }}>
                                Save Changes
                            </Button>
                        </>
                    )}
                </Box>

                <Grid container spacing={{ xs: 2, md: 3 }}>
                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                bgcolor: '#1a1a1a',
                                border: '1px solid rgba(255,255,255,0.06)',
                                borderRadius: 3,
                                p: 2,
                                position: { md: 'sticky' },
                                top: { md: 72 },
                            }}>
                            <Typography
                                sx={{
                                    color: '#ffffff',
                                    fontFamily: FONT_ENUM.BOLD_HEADER,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    px: 1,
                                    py: 1.5,
                                    mb: 0.5,
                                }}>
                                Edit Sections
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {sections.map(({ key, label, Icon }) => {
                                    const active = activeSection === key;
                                    return (
                                        <Box
                                            key={key}
                                            onClick={() => setActiveSection(key)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.5,
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s ease, color 0.15s ease',
                                                ...(active
                                                    ? {
                                                          bgcolor: 'rgba(255,255,255,0.08)',
                                                          color: COMPANY_BRAND_ORANGE,
                                                      }
                                                    : {
                                                          color: 'rgba(255,255,255,0.45)',
                                                          '&:hover': {
                                                              bgcolor: 'rgba(255,255,255,0.06)',
                                                              color: 'rgba(255,255,255,0.9)',
                                                          },
                                                      }),
                                            }}>
                                            <Icon size={20} strokeWidth={active ? 2.5 : 2} />
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.875rem',
                                                    fontWeight: active ? 700 : 500,
                                                    color: 'inherit',
                                                }}>
                                                {label}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        {renderSection()}
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export { CompanyEditContainer };
